import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, Card, CardContent, Button, Chip, Stack, Divider,
  Alert, CircularProgress, Stepper, Step, StepLabel, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalLaundryServiceIcon from "@mui/icons-material/LocalLaundryService";

const API = process.env.REACT_APP_API_LAUNDRY_URL;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } });
const TEAL = "#006D77";

// the normal happy-path pipeline shown as a stepper
const PIPELINE = [
  "pending", "confirmed", "pickup_assigned", "pickup_completed",
  "received_at_laundry", "washing", "drying", "ironing",
  "quality_check", "packed", "out_for_delivery", "delivered",
];
const statusLabel = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const MyLaundryOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(null); // order awaiting cancel confirm

  const load = () => {
    setLoading(true);
    axios.get(`${API}/booking/my-orders`, authHeaders())
      .then((r) => setOrders(r.data || []))
      .catch(() => setError("Could not load your orders."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doCancel = async () => {
    const o = confirmCancel;
    setConfirmCancel(null);
    try {
      await axios.put(`${API}/booking/orders/${o.id}/cancel`, {}, authHeaders());
      load();
    } catch { setError("Could not cancel this order."); }
  };

  if (loading) return <Box textAlign="center" py={8}><CircularProgress sx={{ color: TEAL }} /></Box>;

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
        sx={{ mb: 2, textTransform: "none", color: TEAL, fontWeight: 600 }}>Back</Button>
      <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom>My Laundry Orders</Typography>
      <Typography color="text.secondary" mb={3}>Track your pickups, wash progress, and deliveries.</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2.5}>
        {orders.map((o) => {
          const cancelled = o.status === "cancelled";
          const delivered = o.status === "delivered";
          // index of current status in the pipeline for the stepper
          const activeIdx = PIPELINE.indexOf(o.status);
          const canCancel = !cancelled && !delivered && ["pending", "confirmed"].includes(o.status);

          return (
            <Card key={o.id} variant="outlined" sx={{ borderRadius: 3, opacity: cancelled ? 0.7 : 1 }}>
              <CardContent>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "#E0F2F1",
                      display: "flex", alignItems: "center", justifyContent: "center", color: TEAL }}>
                      <LocalLaundryServiceIcon />
                    </Box>
                    <Box>
                      <Typography fontWeight={800}>{o.service_type}</Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">{o.order_code}</Typography>
                    </Box>
                  </Stack>
                  <Chip label={statusLabel(o.status)}
                    color={cancelled ? "default" : delivered ? "success" : "warning"} size="small" />
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {/* Details */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={1}>
                  <Box flex={1}>
                    <Typography variant="body2"><b>Pickup:</b> {o.pickup_date} · {o.pickup_time}</Typography>
                    <Typography variant="body2"><b>Delivery:</b> {o.delivery_date} · {o.delivery_time}</Typography>
                    <Typography variant="body2"><b>Items:</b> {o.items?.reduce((s, i) => s + i.quantity, 0)} piece(s)</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2"><b>Total:</b> ₹{o.total_amount}</Typography>
                    <Typography variant="body2"><b>Payment:</b> {o.payment_status === "paid" ? "Paid" : "Pay on delivery"} ({o.payment_method})</Typography>
                    {o.pickup_agent_id && <Typography variant="body2"><b>Pickup agent:</b> assigned</Typography>}
                    {o.delivery_agent_id && <Typography variant="body2"><b>Delivery agent:</b> assigned</Typography>}
                  </Box>
                </Stack>

                {/* Progress */}
                {!cancelled ? (
                  <Box sx={{ mt: 2, overflowX: "auto" }}>
                    <Stepper activeStep={activeIdx} alternativeLabel
                      sx={{ minWidth: 900, "& .MuiStepLabel-label": { fontSize: 10 },
                        "& .MuiStepIcon-root.Mui-active": { color: TEAL },
                        "& .MuiStepIcon-root.Mui-completed": { color: TEAL } }}>
                      {PIPELINE.map((s) => <Step key={s}><StepLabel>{statusLabel(s)}</StepLabel></Step>)}
                    </Stepper>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>This order was cancelled.</Alert>
                )}

                {/* Items breakdown */}
                <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2, p: 1.5, bgcolor: "#F8FAFC" }}>
                  {o.items?.map((it, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" py={0.25}>
                      <Typography variant="body2">{it.item} × {it.quantity}</Typography>
                      <Typography variant="body2">₹{it.total}</Typography>
                    </Stack>
                  ))}
                </Paper>

                {canCancel && (
                  <Stack direction="row" spacing={1.5} mt={2}>
                    <Button variant="outlined" color="error" sx={{ textTransform: "none" }}
                      onClick={() => setConfirmCancel(o)}>Cancel Order</Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          );
        })}
        {orders.length === 0 && (
          <Paper variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: "center", bgcolor: "#F8FAFC" }}>
            <LocalLaundryServiceIcon sx={{ fontSize: 56, color: "#94A3B8", mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>No laundry orders yet</Typography>
            <Typography color="text.secondary" mb={2}>Book a service to see it here.</Typography>
            <Button variant="contained" sx={{ bgcolor: TEAL, textTransform: "none" }}
              onClick={() => navigate("/laundry-service")}>Book Laundry</Button>
          </Paper>
        )}
      </Stack>

      {/* Cancel confirm */}
      <Dialog open={!!confirmCancel} onClose={() => setConfirmCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Cancel order?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Cancel order {confirmCancel?.order_code}? This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCancel(null)} sx={{ textTransform: "none" }}>Keep Order</Button>
          <Button variant="contained" color="error" onClick={doCancel} sx={{ textTransform: "none" }}>Cancel Order</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyLaundryOrders;